import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [settings, setSettings] = useState({
    whatsappTemplate: 'Hi {name}, your gym membership is {status}. Please renew to continue your fitness journey! Contact us for more details.',
    emailSubject: 'Gym Membership Renewal Reminder',
    emailTemplate: 'Hi {name},\n\nYour gym membership is {status}.\n\nPlease renew to continue your fitness journey!\n\nThank you,\n{gymName}'
  });
  
  // Invoice Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    gymName: '',
    gymLogo: '',
    gymAddress: '',
    gymPhone: '',
    gymEmail: '',
    gstNumber: '',
    invoicePrefix: 'INV',
    termsAndConditions: 'Thank you for your business!'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
    fetchInvoiceSettings();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings', getAuthHeader());
      if (response.data.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceSettings = async () => {
    try {
      const response = await axios.get('/api/invoices/settings', getAuthHeader());
      if (response.data) {
        setInvoiceSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Failed to fetch invoice settings:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post('/api/settings', settings, getAuthHeader());
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleInvoiceSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post('/api/invoices/settings', invoiceSettings, getAuthHeader());
      setMessage({ type: 'success', text: 'Invoice settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save invoice settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        setMessage({ type: 'error', text: 'Logo file size should be less than 500KB' });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvoiceSettings(prev => ({ ...prev, gymLogo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const placeholders = [
    { key: '{name}', desc: 'Member name' },
    { key: '{phone}', desc: 'Member phone' },
    { key: '{email}', desc: 'Member email' },
    { key: '{status}', desc: 'Membership status (e.g., expiring in 5 days, expired 3 days ago)' },
    { key: '{expiryDate}', desc: 'Expiry date' },
    { key: '{gymName}', desc: 'Gym name' }
  ];

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>⚙️ Admin Settings</h1>
      </div>

      {message.text && (
        <div style={{
          padding: '15px 20px',
          marginBottom: '20px',
          borderRadius: '8px',
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828',
          border: `1px solid ${message.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '0', 
        marginBottom: '20px',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('templates')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'templates' ? '#4CAF50' : 'transparent',
            color: activeTab === 'templates' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
        >
          📝 Message Templates
        </button>
        <button
          onClick={() => setActiveTab('invoice')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'invoice' ? '#4CAF50' : 'transparent',
            color: activeTab === 'invoice' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
        >
          🧾 Invoice Settings
        </button>
      </div>

      {/* Message Templates Tab */}
      {activeTab === 'templates' && (
      <div style={{ display: 'grid', gap: '30px', maxWidth: '900px' }}>
        {/* Message Templates Section */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #4CAF50', paddingBottom: '10px' }}>
            📝 Message Templates
          </h2>

          {/* Placeholders Guide */}
          <div style={{
            background: '#f5f5f5',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '25px'
          }}>
            <h4 style={{ marginBottom: '10px', color: '#555' }}>Available Placeholders:</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {placeholders.map(p => (
                <span key={p.key} style={{
                  background: '#fff',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  border: '1px solid #ddd'
                }}>
                  <code style={{ color: '#4CAF50', fontWeight: 'bold' }}>{p.key}</code>
                  <span style={{ color: '#666', marginLeft: '5px' }}>- {p.desc}</span>
                </span>
              ))}
            </div>
          </div>

          <form onSubmit={handleSave}>
            {/* WhatsApp Template */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Message Template
              </label>
              <textarea
                value={settings.whatsappTemplate || ''}
                onChange={(e) => setSettings({ ...settings, whatsappTemplate: e.target.value })}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter default WhatsApp message..."
              />
            </div>

            {/* Email Subject */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                ✉️ Email Subject
              </label>
              <input
                type="text"
                value={settings.emailSubject || ''}
                onChange={(e) => setSettings({ ...settings, emailSubject: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="Email subject line..."
              />
            </div>

            {/* Email Template */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                ✉️ Email Body Template
              </label>
              <textarea
                value={settings.emailTemplate || ''}
                onChange={(e) => setSettings({ ...settings, emailTemplate: e.target.value })}
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter default email body..."
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 30px',
                background: saving ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {saving ? '⏳ Saving...' : '💾 Save Settings'}
            </button>
          </form>
        </div>

        {/* Preview Section */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #2196F3', paddingBottom: '10px' }}>
            👁️ Preview (Sample)
          </h2>
          
          <div style={{ display: 'grid', gap: '20px' }}>
            <div>
              <h4 style={{ color: '#25D366', marginBottom: '8px' }}>WhatsApp Preview:</h4>
              <div style={{
                background: '#DCF8C6',
                padding: '15px',
                borderRadius: '10px',
                fontSize: '14px',
                whiteSpace: 'pre-wrap'
              }}>
                {settings.whatsappTemplate
                  .replace('{name}', 'Sachin')
                  .replace('{status}', 'expiring in 3 days')
                  .replace('{phone}', '9876543210')
                  .replace('{email}', 'sachin@email.com')
                  .replace('{expiryDate}', '15-Dec-2025')
                  .replace('{gymName}', 'GYMkhata')}
              </div>
            </div>

            <div>
              <h4 style={{ color: '#2196F3', marginBottom: '8px' }}>Email Preview:</h4>
              <div style={{
                background: '#f0f0f0',
                padding: '15px',
                borderRadius: '10px',
                fontSize: '14px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
                  Subject: {settings.emailSubject
                    .replace('{name}', 'Sachin')
                    .replace('{status}', 'expiring in 3 days')
                    .replace('{gymName}', 'GYMkhata')}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {settings.emailTemplate
                    .replace('{name}', 'Sachin')
                    .replace('{status}', 'expiring in 3 days')
                    .replace('{phone}', '9876543210')
                    .replace('{email}', 'sachin@email.com')
                    .replace('{expiryDate}', '15-Dec-2025')
                    .replace('{gymName}', 'GYMkhata')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Invoice Settings Tab */}
      {activeTab === 'invoice' && (
      <div style={{ display: 'grid', gap: '30px', maxWidth: '900px' }}>
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #4CAF50', paddingBottom: '10px' }}>
            🧾 Invoice Settings
          </h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Configure your gym details that will appear on invoices.
          </p>

          <div style={{ display: 'grid', gap: '20px' }}>
            {/* Logo Upload */}
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                🖼️ Gym Logo
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {invoiceSettings.gymLogo && (
                  <img 
                    src={invoiceSettings.gymLogo} 
                    alt="Gym Logo" 
                    style={{ width: '80px', height: '80px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '8px' }}
                  />
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  <label 
                    htmlFor="logo-upload"
                    style={{
                      padding: '8px 16px',
                      background: '#f5f5f5',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    📤 Upload Logo
                  </label>
                  <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Max size: 500KB</p>
                </div>
                {invoiceSettings.gymLogo && (
                  <button
                    onClick={() => setInvoiceSettings(prev => ({ ...prev, gymLogo: '' }))}
                    style={{
                      padding: '6px 12px',
                      background: '#ffebee',
                      color: '#c62828',
                      border: '1px solid #ef9a9a',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    ❌ Remove
                  </button>
                )}
              </div>
            </div>

            {/* Gym Name */}
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                🏋️ Gym Name
              </label>
              <input
                type="text"
                value={invoiceSettings.gymName}
                onChange={(e) => setInvoiceSettings(prev => ({ ...prev, gymName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="Enter your gym name"
              />
            </div>

            {/* Gym Address */}
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                📍 Gym Address
              </label>
              <textarea
                value={invoiceSettings.gymAddress}
                onChange={(e) => setInvoiceSettings(prev => ({ ...prev, gymAddress: e.target.value }))}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter complete gym address"
              />
            </div>

            {/* Phone and Email in grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                  📞 Phone Number
                </label>
                <input
                  type="text"
                  value={invoiceSettings.gymPhone}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, gymPhone: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                  ✉️ Email Address
                </label>
                <input
                  type="email"
                  value={invoiceSettings.gymEmail}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, gymEmail: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Email address"
                />
              </div>
            </div>

            {/* GST and Invoice Prefix in grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                  🧾 GST Number
                </label>
                <input
                  type="text"
                  value={invoiceSettings.gstNumber}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, gstNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., 22AAAAA0000A1Z5"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                  🔢 Invoice Prefix
                </label>
                <input
                  type="text"
                  value={invoiceSettings.invoicePrefix}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoicePrefix: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., INV-"
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                📋 Terms & Conditions
              </label>
              <textarea
                value={invoiceSettings.termsAndConditions}
                onChange={(e) => setInvoiceSettings(prev => ({ ...prev, termsAndConditions: e.target.value }))}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Enter terms and conditions to display on invoices"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleInvoiceSave}
              disabled={saving}
              style={{
                padding: '12px 30px',
                background: saving ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '10px'
              }}
            >
              {saving ? '⏳ Saving...' : '💾 Save Invoice Settings'}
            </button>
          </div>
        </div>

        {/* Invoice Preview */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #2196F3', paddingBottom: '10px' }}>
            👁️ Invoice Header Preview
          </h2>
          <div style={{ 
            border: '1px solid #ddd', 
            borderRadius: '8px', 
            padding: '20px',
            background: '#fafafa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {invoiceSettings.gymLogo && (
                  <img 
                    src={invoiceSettings.gymLogo} 
                    alt="Logo" 
                    style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                  />
                )}
                <div>
                  <h3 style={{ margin: 0, color: '#333' }}>{invoiceSettings.gymName || 'Your Gym Name'}</h3>
                  <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#666', whiteSpace: 'pre-line' }}>
                    {invoiceSettings.gymAddress || 'Your gym address will appear here'}
                  </p>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '13px', color: '#666' }}>
                <p style={{ margin: '0' }}>📞 {invoiceSettings.gymPhone || 'Phone'}</p>
                <p style={{ margin: '3px 0' }}>✉️ {invoiceSettings.gymEmail || 'Email'}</p>
                {invoiceSettings.gstNumber && (
                  <p style={{ margin: '3px 0', fontWeight: 'bold' }}>GST: {invoiceSettings.gstNumber}</p>
                )}
              </div>
            </div>
            <div style={{ borderTop: '1px dashed #ddd', marginTop: '15px', paddingTop: '10px', fontSize: '12px', color: '#888' }}>
              Invoice Number: <strong>{invoiceSettings.invoicePrefix || 'INV-'}001</strong>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminSettings;
